import {
  Badge,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Skeleton,
} from '@design-system';
import { Download, FileText, Search } from 'lucide-react';
import { formatCurrency, getQuotePaymentOptionDisplayLabel } from '@shared';
import {
  useIllustrationsPanel,
  IllustrationStatusFilter,
} from '@/features/shop-insurance/hooks/use-client-illustrations';
import {
  getIllustrationDisplayStatus,
  IllustrationDisplayStatus,
  ILLUSTRATION_DISPLAY_STATUS_LABELS,
} from '@/features/shop-insurance/lib/illustration-display-status';
import { ClientIllustration } from '@/features/shop-insurance/types';

interface IllustrationsPanelProps {
  clientId: string | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const statusBadgeClasses: Record<IllustrationDisplayStatus, string> = {
  ready: 'bg-green-100 text-green-800',
  generating: 'bg-amber-100 text-amber-800',
  expired: 'bg-gray-100 text-gray-600',
  failed: 'bg-red-100 text-red-800',
};

const iconTileClasses: Record<IllustrationDisplayStatus, string> = {
  ready: 'bg-primary-50 text-primary',
  generating: 'bg-amber-50 text-amber-600',
  expired: 'bg-gray-100 text-gray-400',
  failed: 'bg-red-50 text-red-500',
};

const STATUS_FILTER_OPTIONS: { value: IllustrationStatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'ready', label: 'Ready' },
  { value: 'generating', label: 'Generating' },
  { value: 'expired', label: 'Expired' },
  { value: 'failed', label: 'Failed' },
];

const PREMIUM_PHRASES: Record<string, string> = {
  'Single Pay': 'Single Pay',
  '5-Pay': '5-Pay Premium',
  '10-Pay': '10-Pay Premium',
  '20-Pay': '20-Pay Premium',
  Annually: 'Annual Premium',
  Monthly: 'Monthly Premium',
};

function formatGeneratedDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatStartedAgo(iso: string) {
  const elapsedMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.max(Math.round(elapsedMs / 60_000), 0);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  return `${hours} hr ago`;
}

function premiumLabel(illustration: ClientIllustration) {
  const amount =
    illustration.requestedPremiumAmount ?? Number(illustration.quote?.premiumAmount);
  if (!Number.isFinite(amount)) return null;
  const paymentOption = getQuotePaymentOptionDisplayLabel({
    cobPaymentOption: illustration.quote?.cobPaymentOption,
    premiumFrequency: illustration.quote?.premiumFrequency,
  });
  const phrase = (paymentOption && PREMIUM_PHRASES[paymentOption]) || 'Premium';
  return `${formatCurrency(amount)} ${phrase}`;
}

function metaLine(illustration: ClientIllustration, status: IllustrationDisplayStatus) {
  switch (status) {
    case 'generating':
      return `Generating… started ${formatStartedAgo(illustration.createdDateTime)}`;
    case 'ready':
      return `Generated ${formatGeneratedDate(illustration.responseDateTime ?? illustration.createdDateTime)} · PDF`;
    case 'expired':
      return `Generated ${formatGeneratedDate(illustration.responseDateTime ?? illustration.createdDateTime)} · Expired`;
    case 'failed':
      return `Failed ${formatGeneratedDate(illustration.createdDateTime)}`;
  }
}

function IllustrationListItem({ illustration }: { illustration: ClientIllustration }) {
  const status = getIllustrationDisplayStatus(illustration);
  const carrierName = illustration.quote?.carrierName;
  const premium = premiumLabel(illustration);
  const subtitle = [carrierName, premium].filter(Boolean).join(' · ');

  const handleDownload = () => {
    if (illustration.pdfUrl) {
      window.open(illustration.pdfUrl, '_blank');
    }
  };

  return (
    <div className="flex items-start gap-3 border-b py-4 last:border-b-0">
      <div
        className={`flex size-11 shrink-0 items-center justify-center rounded-lg ${iconTileClasses[status]}`}
      >
        <FileText className="size-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-primary-dark truncate font-semibold">
            {illustration.quote?.productName ?? 'Illustration'}
          </span>
          <Badge className={`shrink-0 ${statusBadgeClasses[status]}`}>
            {ILLUSTRATION_DISPLAY_STATUS_LABELS[status]}
          </Badge>
        </div>
        {subtitle && <p className="text-foreground truncate text-sm">{subtitle}</p>}
        <p className="text-muted-foreground text-xs">{metaLine(illustration, status)}</p>
      </div>
      <Button
        variant="outline"
        size="icon-sm"
        onClick={handleDownload}
        disabled={status !== 'ready' || !illustration.pdfUrl}
        aria-label="Download illustration"
      >
        <Download />
      </Button>
    </div>
  );
}

export function IllustrationsPanel({ clientId, open, onOpenChange }: IllustrationsPanelProps) {
  const {
    illustrations,
    totalCount,
    isLoading,
    isError,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
  } = useIllustrationsPanel(clientId);

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
    >
      <SheetContent className="flex w-full flex-col gap-0 sm:max-w-lg">
        <SheetHeader className="pb-4">
          <SheetTitle className="text-primary-dark flex items-center gap-2 text-xl">
            Illustrations
            {totalCount > 0 && <Badge className="rounded-full">{totalCount}</Badge>}
          </SheetTitle>
          <SheetDescription>Generated illustration documents for this client</SheetDescription>
        </SheetHeader>

        <div className="flex items-center gap-2 px-4 pb-2">
          <div className="relative flex-1">
            <Search className="text-muted-foreground absolute left-3 top-1/2 size-4 -translate-y-1/2" />
            <Input
              placeholder="Search illustrations"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select
            value={statusFilter}
            onValueChange={value => setStatusFilter(value as IllustrationStatusFilter)}
          >
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTER_OPTIONS.map(option => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1 overflow-y-auto px-4">
          {isLoading ? (
            <div className="space-y-4 py-4">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : isError ? (
            <p className="text-muted-foreground py-8 text-center text-sm">
              Could not load illustrations. Please try again.
            </p>
          ) : illustrations.length === 0 ? (
            <p className="text-muted-foreground py-8 text-center text-sm">
              {totalCount === 0
                ? 'No illustrations have been generated for this client yet.'
                : 'No illustrations match your search.'}
            </p>
          ) : (
            illustrations.map(illustration => (
              <IllustrationListItem
                key={illustration.requestId}
                illustration={illustration}
              />
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
